import { ApolloProvider } from '@apollo/client';
import { SWRConfig } from 'swr';
import React, { useEffect } from 'react';
import Router from 'next/router';
import Head from 'next/head';
import { ToastContainer } from 'react-toastify';
import { GoogleTagManager } from '@next/third-parties/google';

import client from '@lib/apolloClient';
import ErrorBoundary from '@components/error-boundary';
import ChatWidget from '@components/chat-widget';
import { shouldLoadGTM } from '@lib/gtm-utils';
import { SibcyListingProvider } from '@lib/sibcy-listing-context';
import { UserProvider } from '@lib/user-context';
import '../styles/libs/sanitize.scss';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/global-styles.scss';
import '../styles/global-utilities.scss';
import '../styles/notification.scss';

import localFont from 'next/font/local';

const gothamBook = localFont({
  fallback: ['system-ui', 'sans-serif'],
  src: '../fonts/Gotham-Book.woff2',
  style: 'normal',
  weight: '400'
});

const gothamBold = localFont({
  fallback: ['system-ui', 'sans-serif'],
  src: '../fonts/Gotham-Bold.woff2',
  style: 'normal',
  weight: '700'
});

const gothamMedium = localFont({
  fallback: ['system-ui', 'sans-serif'],
  src: '../fonts/Gotham-Medium.woff2',
  style: 'normal',
  weight: '400'
});

const ttJenevers = localFont({
  fallback: ['system-ui', 'sans-serif'],
  src: '../fonts/TTJenevers-Regular.woff2',
  style: 'normal',
  weight: '400'
});

const AppContent = ({ Component, pageProps }) => {
  // Only load GTM on production domain (www.sibcycline.com)
  const loadGTM = shouldLoadGTM();

  useEffect(() => {
    // Set returnTo cookie on initial page load (covers hard navigations like <a href>)
    document.cookie = `returnTo=${window.location.pathname}; path=/`;

    const handleRouteChange = (url) => {
      document.cookie = `returnTo=${url}; path=/`;
    };
    Router.events.on('routeChangeComplete', handleRouteChange);
    return () => Router.events.off('routeChangeComplete', handleRouteChange);
  }, []);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="format-detection" content="telephone=no, date=no, email=no, address=no" />
      </Head>
      <style jsx global>{`
        :root {
          --font-family-base: ${gothamBook.style.fontFamily};
          --font-family-bold: ${gothamBold.style.fontFamily};
          --font-family-medium: ${gothamMedium.style.fontFamily};
          --font-family-headings: ${ttJenevers.style.fontFamily};
        }
        * {
          font-variant-ligatures: no-contextual;
        }
      `}</style>

      <ErrorBoundary>
        <ApolloProvider client={client}>
          <SibcyListingProvider>
            {loadGTM && <GoogleTagManager gtmId="GTM-MQ6LP4L" />}
            <ChatWidget />
            <ToastContainer position="bottom-right" autoClose={5000} />
            <Component {...pageProps} />
          </SibcyListingProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  );
};

const MyApp = ({ Component, pageProps }) => {
  const { user } = pageProps;

  return (
    <SWRConfig
      value={{
        shouldRetryOnError: false,
        errorRetryCount: 0,
        onErrorRetry: () => {},
        revalidateOnFocus: false,
        // Caution: with revalidateIfStale off, any hook seeded via fallback/fallbackData never fetches
        revalidateIfStale: false,
        dedupingInterval: 60000,
        fallback: user ? { '/auth/profile': user } : {}
      }}
    >
      <UserProvider>
        <AppContent Component={Component} pageProps={pageProps} />
      </UserProvider>
    </SWRConfig>
  );
};

export default MyApp;
